import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Container,
  Form,
  Row,
  Col,
  Card,
} from "react-bootstrap";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import jsPDF from "jspdf";

const PaymentList = () => {
  const [payments, setPayments] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("unpaid");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalPayments, setTotalPayments] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  const token = localStorage.getItem("admin_token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };

  useEffect(() => {
    fetchPayments();
  }, [currentPage, rowsPerPage, statusFilter, search, startDate, endDate]);

  const fetchPayments = async () => {
    setIsLoading(true);
    try {
      const params = {
        page: currentPage,
        per_page: rowsPerPage,
        status: statusFilter !== 'all' ? statusFilter : undefined,
        search: search || undefined,
        start: startDate || undefined,
        end: endDate || undefined,
      };

      const res = await axios.get(`${API_BASE}/api/payments/enriched`, {
        headers,
        params,
      });

      setPayments(res.data.payments);
      setTotalPayments(res.data.total);
    } catch (err) {
      console.error("Failed to fetch payments", err);
    } finally {
      setIsLoading(false);
    }
  };

  const getFormattedDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const generateInvoiceNumber = () => {
    return `SAL-${Math.floor(1000 + Math.random() * 9000)}`;
  };

  const downloadDocument = (payment, type) => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  
    // Company Header
    doc.setFontSize(20).setTextColor(40, 40, 40).setFont('helvetica', 'bold')
       .text('SALPITA INC', 105, 20, { align: 'center' });
  
    doc.setFontSize(10).setFont('helvetica', 'normal');
    doc.text('910 20th Street West, Saskatoon, SK S7M 0Y4, Canada', 105, 28, { align: 'center' });
    doc.text('Phone: 306.373.7482', 105, 32, { align: 'center' });
  
    // Title
    doc.setFontSize(16).setFont('helvetica', 'bold')
       .text(type === 'invoice' ? 'INVOICE' : 'RECEIPT', 105, 45, { align: 'center' });
  
    doc.setDrawColor(200, 200, 200).line(15, 50, 195, 50);
  
    // Invoice Info
    doc.setFontSize(10).setFont('helvetica', 'normal');
    doc.text(`Invoice #: ${generateInvoiceNumber()}`, 20, 60);
    doc.text(`Date: ${getFormattedDate(payment.order_date)}`, 20, 65);
  
    // Customer Box
    doc.setFillColor(245, 245, 245).rect(15, 75, 180, 20, 'F');
    doc.setDrawColor(200, 200, 200).rect(15, 75, 180, 20);
    doc.setFont('helvetica', 'bold').text('Bill To:', 20, 82);
    doc.setFont('helvetica', 'normal');
    doc.text(`${payment.customer_name}`, 20, 87);
    doc.text('Address not available', 20, 92);
  
    // Table Header
    const headers = ['Description', 'Quantity', 'Unit Price', 'Total'];
    const data = [
      ['Order Total', '1', `$${parseFloat(payment.order_total).toFixed(2)}`, `$${parseFloat(payment.order_total).toFixed(2)}`]
    ];
  
    if (type === 'receipt') {
      data.push(['Payment Received', '1', `$${parseFloat(payment.paid_amount).toFixed(2)}`, `$${parseFloat(payment.paid_amount).toFixed(2)}`]);
    }
  
    let y = 105;
    doc.setFillColor(60, 60, 60);
    doc.setTextColor(255, 255, 255);
    doc.rect(15, y, 180, 8, 'F');
    doc.setFont('helvetica', 'bold');
    doc.text(headers[0], 20, y + 6);
    doc.text(headers[1], 100, y + 6, { align: 'right' });
    doc.text(headers[2], 140, y + 6, { align: 'right' });
    doc.text(headers[3], 180, y + 6, { align: 'right' });
  
    y += 10;
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'normal');
  
    data.forEach(row => {
      doc.text(row[0], 20, y + 6);
      doc.text(row[1], 100, y + 6, { align: 'right' });
      doc.text(row[2], 140, y + 6, { align: 'right' });
      doc.text(row[3], 180, y + 6, { align: 'right' });
      y += 8;
    });
  
    // Totals Section
    y += 8;
    const tax = (parseFloat(payment.order_total) * 0.05).toFixed(2);
    const grandTotal = (parseFloat(payment.order_total) + parseFloat(tax)).toFixed(2);
    const balance = (parseFloat(payment.order_total) - parseFloat(payment.paid_amount)).toFixed(2);
  
    doc.setFont('helvetica', 'bold');
    doc.text('Subtotal:', 160, y, { align: 'right' });
    doc.text(`$${parseFloat(payment.order_total).toFixed(2)}`, 180, y, { align: 'right' });
    y += 6;
  
    doc.text('Tax (5%):', 160, y, { align: 'right' });
    doc.text(`$${tax}`, 180, y, { align: 'right' });
    y += 6;
  
    doc.setFontSize(12).text('Total:', 160, y, { align: 'right' });
    doc.text(`$${grandTotal}`, 180, y, { align: 'right' });
  
    if (type === 'receipt') {
      y += 6;
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(balance > 0 ? 200 : 0, 0, 0);
      doc.text('Balance Due:', 160, y, { align: 'right' });
      doc.text(`$${balance}`, 180, y, { align: 'right' });
    }
  
    // Footer
    y = 280;
    doc.setFontSize(8).setFont('helvetica', 'normal').setTextColor(100, 100, 100);
    doc.text('Thank you for your business!', 105, y, { align: 'center' });
    doc.text('SALPITA INC - 910 20th Street West, Saskatoon, SK S7M 0Y4, Canada - 306.373.7482', 105, y + 5, { align: 'center' });
  
    doc.save(`${type}_${payment.order_id}.pdf`);
  };

  const totalPages = Math.ceil(totalPayments / rowsPerPage);

  return (
    <Container className="mt-4">
      <h3 className="mb-3">Payment Records</h3>

      <Row className="mb-3">
        <Col md={3}>
          <Form.Control
            type="text"
            placeholder="Search by order or customer"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="unpaid">Unpaid</option>
            <option value="partial paid">Partial Paid</option>
            <option value="paid">Paid</option>
            <option value="all">All</option>
          </Form.Select>
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Control
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </Col>
        <Col md={2}>
          <Form.Select value={rowsPerPage === 9999 ? "all" : rowsPerPage} onChange={(e) => {
            setRowsPerPage(e.target.value === "all" ? 9999 : parseInt(e.target.value));
            setCurrentPage(1);
          }}>
            <option value="10">10 per page</option>
            <option value="20">20 per page</option>
            <option value="50">50 per page</option>
            <option value="all">Show all</option>
          </Form.Select>
        </Col>
      </Row>

      <Table bordered striped hover responsive>
        <thead>
          <tr>
            <th>#</th>
            <th>Order ID</th>
            <th>Customer</th>
            <th>Order Total</th>
            <th>Paid</th>
            <th>Balance</th>
            <th>Status</th>
            <th>Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p, i) => {
            const orderTotal = parseFloat(p.order_total) || 0;
            const paidAmount = parseFloat(p.paid_amount) || 0;
            const balance = (orderTotal - paidAmount).toFixed(2);
            const hasPayment = paidAmount > 0;

            return (
              <tr key={p.payment_id}>
                <td>{(currentPage - 1) * rowsPerPage + i + 1}</td>
                <td>{p.order_id}</td>
                <td>{p.customer_name}</td>
                <td>${orderTotal.toFixed(2)}</td>
                <td>${paidAmount.toFixed(2)}</td>
                <td style={{ color: balance > 0 ? "red" : "green", fontWeight: "bold" }}>${balance}</td>
                <td>{p.payment_status}</td>
                <td>{getFormattedDate(p.order_date)}</td>
                <td>
                  <div className="d-flex flex-wrap gap-2">
                    <Button variant="outline-primary" size="sm" onClick={() => downloadDocument(p, 'invoice')}>Invoice</Button>
                    {hasPayment && (
                      <Button variant="outline-success" size="sm" onClick={() => downloadDocument(p, 'receipt')}>Receipt</Button>
                    )}
                    <Button variant="outline-warning" size="sm" onClick={() => navigate(`/admin/payments/update/${p.payment_id}`, {
                      state: {
                        payment_id: p.payment_id,
                        order_id: p.order_id,
                        customer_id: p.customer_id,
                        grand_total: p.order_total
                      }
                    })}>Update Payment</Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      {rowsPerPage !== 9999 && (
        <div className="d-flex justify-content-between align-items-center mt-3">
          <span>
            Showing {(currentPage - 1) * rowsPerPage + 1}–{Math.min(currentPage * rowsPerPage, totalPayments)} of {totalPayments}
          </span>
          <div>
            <Button variant="outline-secondary" className="me-2" disabled={currentPage === 1} onClick={() => setCurrentPage(1)}>First</Button>
            <Button variant="outline-secondary" className="me-2" disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Previous</Button>
            <span className="mx-2">Page {currentPage} of {totalPages}</span>
            <Button variant="outline-secondary" className="me-2" disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)}>Next</Button>
            <Button variant="outline-secondary" disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)}>Last</Button>
          </div>
        </div>
      )}
    </Container>
  );
};

export default PaymentList;
